<template>
  <div class="aw-page" data-testid="analytics-workspace-shell">
    <!-- Screen-reader live region: async state transitions are announced (US-64 AC8) -->
    <div class="aw-sr-only" aria-live="polite" role="status" data-testid="aw-live-region">{{ store.announce }}</div>

    <header class="aw-header">
      <div>
        <p class="aw-kicker">Analytics</p>
        <h1 class="aw-title" tabindex="-1" ref="pageHeading">Operating Workspace</h1>
      </div>
      <div class="aw-header-actions">
        <!-- Freshness + provenance shown only when valid (US-60 AC4 / US-64 AC6) -->
        <span
          v-if="store.freshness"
          class="aw-freshness"
          :class="`aw-fresh-${store.freshness.stale ? 'stale' : 'healthy'}`"
          :data-aw-fresh="store.freshness.stale ? 'stale' : 'healthy'"
          data-testid="aw-freshness"
        >
          <span class="aw-fresh-dot" aria-hidden="true"></span>
          {{ store.freshness.stale ? 'Data delayed' : 'Data fresh' }}
          <span class="aw-fresh-meta">· {{ relativeTime(store.freshness.lastIngestAt) }}</span>
        </span>
        <button
          v-if="canManage"
          type="button"
          class="aw-btn aw-btn-ghost"
          data-testid="aw-config-toggle"
          @click="toggleConfig"
        >Configure cards</button>
        <button
          type="button"
          class="aw-btn aw-btn-ghost"
          data-testid="aw-refresh"
          :disabled="store.loading"
          @click="refresh"
        >Refresh</button>
      </div>
    </header>

    <!-- Card configuration (admin) — visibility toggle + reorder, persisted per site (US-60 AC5/AC6) -->
    <section v-if="configOpen && canManage" class="aw-config" data-testid="aw-config-panel">
      <h2 class="aw-section-title">Configure trend cards</h2>
      <ul class="aw-config-list">
        <li v-for="(item, idx) in configDraft" :key="item.key" class="aw-config-row" :data-config-key="item.key">
          <label class="aw-config-check">
            <input type="checkbox" v-model="item.visible" :data-testid="`aw-config-card-toggle-${item.key}`" />
            {{ item.label }}
          </label>
          <span class="aw-config-move">
            <button type="button" class="aw-icon-btn" :disabled="idx === 0" :data-testid="`aw-config-move-up-${item.key}`" @click="move(idx, -1)" aria-label="Move up">↑</button>
            <button type="button" class="aw-icon-btn" :disabled="idx === configDraft.length - 1" :data-testid="`aw-config-move-down-${item.key}`" @click="move(idx, 1)" aria-label="Move down">↓</button>
          </span>
        </li>
      </ul>
      <div class="aw-config-actions">
        <button type="button" class="aw-btn aw-btn-primary" data-testid="aw-config-save" @click="saveConfig">Save configuration</button>
        <button type="button" class="aw-btn aw-btn-ghost" @click="configOpen = false">Cancel</button>
      </div>
    </section>

    <!-- Exactly one state renders (US-60 AC2 / US-64 AC2) -->
    <div class="aw-body" :data-aw-state="store.uiState">
      <!-- Loading -->
      <div v-if="store.uiState === 'loading'" class="aw-state" data-testid="aw-state-loading" data-aw-state-branch="loading">
        <div class="aw-spinner" aria-hidden="true"></div>
        <p>Loading your workspace…</p>
      </div>

      <!-- Error (actionable copy, not raw strings — US-64 AC9) with safe retry (US-64 AC4) -->
      <div v-else-if="store.uiState === 'error'" class="aw-state aw-state-error" data-testid="aw-state-error" data-aw-state-branch="error">
        <p class="aw-error-msg">{{ store.error }}</p>
        <button type="button" class="aw-btn aw-btn-primary" data-testid="aw-retry" :disabled="store.loading" @click="refresh">Retry</button>
      </div>

      <!-- Empty: explains the no-data condition + a neutral next step (US-64 AC12) -->
      <div v-else-if="store.uiState === 'empty'" class="aw-state aw-state-empty" data-testid="aw-state-empty" data-aw-state-branch="empty">
        <h2>No signals to show yet</h2>
        <p>Analysis is currently unavailable for this site. Once enough activity is recorded, trend cards and anomalies will appear here.</p>
        <button type="button" class="aw-btn aw-btn-ghost" data-testid="aw-empty-retry" @click="refresh">Check again</button>
      </div>

      <!-- Ready -->
      <div v-else class="aw-ready" data-testid="aw-state-ready" data-aw-state-branch="ready">
        <!-- Trend cards -->
        <section class="aw-cards" data-testid="trend-cards-section" aria-label="Trend cards">
          <article
            v-for="card in store.cards"
            :key="card.metricKey"
            class="aw-card"
            :class="{ 'aw-card-adverse': card.isAdverse }"
            data-testid="trend-card"
            :data-metric="card.metricKey"
          >
            <div class="aw-card-top">
              <span class="aw-card-label" data-testid="trend-metric-label">{{ card.label }}</span>
              <!-- Per-card freshness (US-60 AC4): explicit text, 3 states -->
              <span
                class="aw-card-fresh"
                :class="`aw-cf-${cardFreshness(card).state}`"
                :data-aw-card-fresh="cardFreshness(card).state"
                data-testid="trend-freshness"
              >{{ cardFreshness(card).label }}</span>
            </div>
            <div class="aw-card-value" data-testid="trend-current-value">{{ formatValue(card.currentValue, card) }}</div>
            <div v-if="card.sufficientData" class="aw-card-signal" :class="signalClass(card)" data-testid="trend-signal">
              <span aria-hidden="true">{{ arrow(card.direction) }}</span>
              {{ formatPct(card.deltaPct) }}
              <span class="aw-card-conf" data-testid="trend-confidence">· {{ Math.round(card.confidence * 100) }}% conf</span>
            </div>
            <div v-else class="aw-card-signal aw-signal-flat" data-testid="trend-signal">{{ card.guidance || 'Not enough data' }}</div>
            <button
              type="button"
              class="aw-card-cta"
              data-testid="trend-investigate-action"
              @click="investigate(card.metricKey)"
            >Investigate</button>
          </article>
        </section>

        <div class="aw-columns">
          <!-- Anomaly panel: routing outcome + rationale (US-61 AC8) -->
          <section class="aw-panel" data-testid="anomaly-panel">
            <h2 class="aw-section-title">Anomalies</h2>
            <p v-if="!store.anomalies.length" class="aw-muted" data-testid="anomaly-empty">No anomalies detected in the current window.</p>
            <ul v-else class="aw-list">
              <li
                v-for="a in store.anomalies"
                :key="a.id"
                class="aw-anomaly"
                data-testid="anomaly-item"
                :data-metric="a.metricKey"
              >
                <div class="aw-anomaly-head">
                  <span class="aw-sev" :class="`aw-sev-${a.severity}`" data-testid="anomaly-severity">{{ a.severity }}</span>
                  <span class="aw-anomaly-label">{{ a.label }}</span>
                  <span
                    class="aw-dest"
                    :class="`aw-dest-${a.destination}`"
                    data-testid="anomaly-destination"
                  >{{ a.destination === 'auto_queue' ? 'Auto-queued' : 'Manual triage' }}</span>
                </div>
                <p class="aw-anomaly-reason" data-testid="anomaly-reason">
                  {{ formatPct(a.deltaPct) }} · {{ Math.round(a.confidence * 100) }}% confidence · {{ a.reason }}
                </p>
                <button type="button" class="aw-btn aw-btn-sm" data-testid="anomaly-investigate" @click="investigate(a.metricKey)">Drill down</button>
              </li>
            </ul>
          </section>

          <!-- Action queue with lifecycle -->
          <section class="aw-panel" data-testid="action-queue">
            <h2 class="aw-section-title">Action queue <span class="aw-count">{{ store.actions.length }}</span></h2>
            <p v-if="!store.actions.length" class="aw-muted" data-testid="action-queue-empty">No queued actions. Investigate an anomaly to draft one.</p>
            <ul v-else class="aw-list">
              <li v-for="act in store.actions" :key="act.id" class="aw-action" data-testid="action-item" :data-status="act.status">
                <div class="aw-action-head">
                  <span class="aw-status" :class="`aw-status-${act.status}`" data-testid="action-status">{{ statusLabel(act.status) }}</span>
                  <span class="aw-action-src">{{ act.source === 'auto' ? 'auto' : 'manual' }}</span>
                </div>
                <p class="aw-action-title">{{ act.title }}</p>
                <p class="aw-action-meta">
                  <span v-if="act.owner">👤 {{ act.owner }}</span>
                  <span class="aw-prio" :class="`aw-prio-${act.priority}`">{{ act.priority }}</span>
                  <span v-if="act.expectedImpact" class="aw-impact">Impact: {{ act.expectedImpact }}</span>
                </p>
                <p v-if="act.verification" class="aw-verif" :class="`aw-verif-${act.verification.result}`" data-testid="action-verification">
                  {{ act.verification.result === 'verified' ? '✓ Verified' : '✗ Not verified' }}
                  ({{ formatPct(act.verification.deltaPct) }})
                </p>
                <!-- Lifecycle controls — write actions, admin only, disabled on untrusted data (US-64 AC7) -->
                <div v-if="canManage" class="aw-action-controls">
                  <button v-if="act.status === 'queued'" type="button" class="aw-btn aw-btn-sm" data-testid="action-start" :disabled="!store.dataTrusted || store.saving" @click="start(act)">Start</button>
                  <button v-if="isVerifiable(act.status)" type="button" class="aw-btn aw-btn-sm aw-btn-primary" data-testid="action-verify" :disabled="!store.dataTrusted || store.saving" @click="openVerify(act)">Verify</button>
                  <button v-if="!isFinalized(act.status)" type="button" class="aw-btn aw-btn-sm aw-btn-ghost" data-testid="action-dismiss" :disabled="store.saving" @click="dismiss(act)">Dismiss</button>
                </div>
                <!-- Inline verify form -->
                <form v-if="verifyingId === act.id" class="aw-verify-form" data-testid="action-verify-form" @submit.prevent="submitVerify(act)">
                  <label class="aw-field-label" :for="`verify-${act.id}`">Observed value (evidence)</label>
                  <input :id="`verify-${act.id}`" v-model="verifyValue" type="number" step="any" class="aw-input" data-testid="verify-post-value" />
                  <textarea v-model="verifyNote" class="aw-input" rows="2" placeholder="Evidence note" data-testid="verify-note"></textarea>
                  <div class="aw-action-controls">
                    <button type="submit" class="aw-btn aw-btn-sm aw-btn-primary" :disabled="store.saving" data-testid="verify-submit">Close &amp; verify</button>
                    <button type="button" class="aw-btn aw-btn-sm aw-btn-ghost" @click="verifyingId = null">Cancel</button>
                  </div>
                </form>
              </li>
            </ul>
            <p v-if="!store.dataTrusted" class="aw-degraded" data-testid="aw-degraded-notice">
              Data is degraded — closing and verifying actions is disabled until a fresh, trusted read is available.
            </p>
          </section>
        </div>
      </div>
    </div>

    <!-- Drill-down drawer — opens in-workspace, does not navigate away (US-62 AC1) -->
    <aside v-if="store.drilldownMetric" class="aw-drawer" data-testid="drilldown-drawer" role="dialog" aria-modal="false" :aria-label="`Drill-down for ${drillContext?.metricName || store.drilldownMetric}`">
      <div class="aw-drawer-head">
        <h2 class="aw-drawer-title" tabindex="-1" ref="drillHeading" data-testid="drilldown-heading">
          {{ drillContext?.metricName || store.drilldownMetric }}
        </h2>
        <button type="button" class="aw-icon-btn" data-testid="drilldown-close" aria-label="Close drill-down" @click="store.closeDrilldown()">✕</button>
      </div>

      <!-- Isolated loading/error — never blocks the main workspace (US-64 AC10) -->
      <div v-if="store.drilldownLoading" class="aw-state" data-testid="drilldown-loading">
        <div class="aw-spinner" aria-hidden="true"></div><p>Loading context…</p>
      </div>
      <div v-else-if="store.drilldownError" class="aw-state aw-state-error" data-testid="drilldown-error">
        <p class="aw-error-msg">{{ store.drilldownError }}</p>
        <button type="button" class="aw-btn aw-btn-sm" data-testid="drilldown-retry" @click="investigate(store.drilldownMetric)">Retry</button>
      </div>

      <div v-else-if="store.drilldown" class="aw-drawer-body">
        <!-- Context: explicit labelled fields; missing → fallback (US-62 AC2/AC3) -->
        <dl class="aw-context" data-testid="drilldown-context">
          <div class="aw-context-row"><dt>Current</dt><dd>{{ ctxVal(drillContext?.currentValue) }}</dd></div>
          <div class="aw-context-row"><dt>Baseline</dt><dd>{{ ctxVal(drillContext?.baselineValue) }}</dd></div>
          <div class="aw-context-row"><dt>Change</dt><dd>{{ drillContext?.deltaDirection || '—' }} {{ drillContext?.deltaMagnitudePct != null ? drillContext.deltaMagnitudePct + '%' : '—' }}</dd></div>
          <div class="aw-context-row"><dt>Affected segment</dt><dd>{{ ctxVal(drillContext?.affectedSegment) }}</dd></div>
          <div class="aw-context-row"><dt>Detection window</dt><dd>{{ drillContext?.detectionWindow ? drillContext.detectionWindow.days + ' days' : '—' }}</dd></div>
          <div class="aw-context-row"><dt>Confidence</dt><dd>{{ drillContext?.confidence != null ? Math.round(drillContext.confidence * 100) + '%' : '—' }}</dd></div>
        </dl>

        <p v-if="!store.drilldown.canGenerateRecommendation" class="aw-degraded" data-testid="drilldown-missing-context">
          Not enough anomaly context to draft a recommendation yet.
        </p>

        <!-- Likely drivers -->
        <div v-if="store.drilldown.drivers && store.drilldown.drivers.length" class="aw-drivers" data-testid="drilldown-drivers">
          <h3 class="aw-sub">Likely drivers</h3>
          <ul><li v-for="(d, i) in store.drilldown.drivers" :key="i">{{ d.note }}</li></ul>
        </div>

        <!-- Ranked recommendations (US-62 AC4) -->
        <div class="aw-recs" data-testid="drilldown-recommendations">
          <h3 class="aw-sub">Recommendations</h3>
          <ul class="aw-list">
            <li
              v-for="rec in store.drilldown.recommendations"
              :key="rec.id"
              class="aw-rec"
              data-testid="recommendation-item"
              :class="{ 'aw-rec-active': draft.recommendationId === rec.id }"
            >
              <div class="aw-rec-head">
                <span class="aw-rec-title">{{ rec.title }}</span>
                <span class="aw-rec-impact">Impact: {{ rec.expectedImpact }} · Effort: {{ rec.effort }}</span>
              </div>
              <p class="aw-rec-rationale">{{ rec.rationale }}</p>
              <button
                v-if="canManage"
                type="button"
                class="aw-btn aw-btn-sm"
                :disabled="!store.drilldown.canGenerateRecommendation"
                data-testid="recommendation-draft"
                @click="startDraft(rec)"
              >Draft action</button>
            </li>
          </ul>
        </div>

        <!-- Draft editor: pre-populated editable execution fields (US-62 AC5/AC6/AC10) -->
        <form v-if="draft.recommendationId && canManage" class="aw-draft" data-testid="draft-editor" @submit.prevent="saveDraft">
          <h3 class="aw-sub">Draft action</h3>
          <label class="aw-field-label" for="draft-title">Title</label>
          <input id="draft-title" v-model="draft.title" class="aw-input" data-testid="draft-field-title" />

          <label class="aw-field-label" for="draft-owner">Owner <span class="aw-req">*</span></label>
          <input id="draft-owner" v-model="draft.owner" class="aw-input" data-testid="draft-field-owner" />
          <p v-if="fieldError('owner')" class="aw-field-err" data-testid="draft-error-owner">{{ fieldError('owner') }}</p>

          <label class="aw-field-label" for="draft-due">Due date</label>
          <input id="draft-due" v-model="draft.dueDate" type="date" class="aw-input" data-testid="draft-field-dueDate" />
          <p v-if="fieldError('dueDate')" class="aw-field-err" data-testid="draft-error-dueDate">{{ fieldError('dueDate') }}</p>

          <label class="aw-field-label" for="draft-priority">Priority</label>
          <select id="draft-priority" v-model="draft.priority" class="aw-input" data-testid="draft-field-priority">
            <option v-for="p in priorities" :key="p" :value="p">{{ p }}</option>
          </select>

          <label class="aw-field-label" for="draft-channel">Execution channel</label>
          <select id="draft-channel" v-model="draft.executionChannel" class="aw-input" data-testid="draft-field-channel">
            <option v-for="c in channels" :key="c" :value="c">{{ c }}</option>
          </select>

          <label class="aw-field-label" for="draft-success">Success criteria</label>
          <input id="draft-success" v-model="draft.successCriteria" class="aw-input" data-testid="draft-field-success" />
          <p v-if="fieldError('successCriteria')" class="aw-field-err" data-testid="draft-error-successCriteria">{{ fieldError('successCriteria') }}</p>

          <!-- Read-only expected impact (US-62 read-only fields) -->
          <p class="aw-readonly" data-testid="draft-readonly-impact">Expected impact (read-only): {{ activeRec?.expectedImpact || '—' }}</p>

          <!-- Save lifecycle states (US-62 AC10) -->
          <div class="aw-draft-actions">
            <button type="submit" class="aw-btn aw-btn-primary" :disabled="store.saving" data-testid="draft-save">
              <span v-if="store.saving" data-testid="draft-save-busy">Saving…</span>
              <span v-else>Queue action</span>
            </button>
            <button type="button" class="aw-btn aw-btn-ghost" @click="cancelDraft">Cancel</button>
          </div>
          <p v-if="store.saveSuccess" class="aw-save-ok" data-testid="draft-save-success">Action queued.</p>
          <p v-if="store.saveError" class="aw-save-err" data-testid="draft-save-error">{{ store.saveError }}</p>
        </form>
      </div>
    </aside>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch, nextTick } from 'vue'
import { useAnalyticsWorkspaceStore } from '../stores/analyticsWorkspace'

const props = defineProps({ id: { type: String, required: true } })
const store = useAnalyticsWorkspaceStore()

const priorities = ['low', 'medium', 'high', 'critical']
const channels = ['manual', 'content_edit', 'seo', 'ads', 'email', 'product']
const STALE_MS = 15 * 60 * 1000

const pageHeading = ref(null)
const drillHeading = ref(null)
const configOpen = ref(false)
const configDraft = ref([])
const verifyingId = ref(null)
const verifyValue = ref('')
const verifyNote = ref('')

const draft = reactive({ recommendationId: null, metricKey: null, title: '', owner: '', dueDate: '', priority: 'medium', executionChannel: 'manual', successCriteria: '' })

const canManage = computed(() => store.permissions.canManage === true)
const drillContext = computed(() => store.drilldown?.context || null)
const activeRec = computed(() => (store.drilldown?.recommendations || []).find((r) => r.id === draft.recommendationId) || null)

function refresh() { store.loadWorkspace(props.id) }

function investigate(metricKey) {
  // Opens the drill-down IN the workspace with metric + site pre-applied
  // (US-60 AC8 / US-62 AC1). Focus moves to the drill-down heading.
  cancelDraft()
  store.loadDrilldown(props.id, metricKey).then(() => {
    nextTick(() => { drillHeading.value?.focus?.() })
  })
}

function startDraft(rec) {
  draft.recommendationId = rec.id
  draft.metricKey = store.drilldownMetric
  draft.title = rec.title
  draft.owner = ''
  draft.dueDate = ''
  draft.priority = (store.drilldown?.anomaly?.severity === 'high') ? 'high' : 'medium'
  draft.executionChannel = 'manual'
  draft.successCriteria = rec.targetDirection && rec.targetDeltaPct != null ? `${rec.targetDirection} by ${Math.round(rec.targetDeltaPct * 100)}%` : ''
  store.saveError = null
  store.saveFieldErrors = []
  store.saveSuccess = false
}

function cancelDraft() {
  draft.recommendationId = null
  store.saveError = null
  store.saveFieldErrors = []
  store.saveSuccess = false
}

async function saveDraft() {
  const action = await store.queueAction(props.id, {
    metricKey: draft.metricKey,
    recommendationId: draft.recommendationId,
    title: draft.title,
    owner: draft.owner,
    dueDate: draft.dueDate || undefined,
    priority: draft.priority,
    executionChannel: draft.executionChannel,
    successCriteria: draft.successCriteria || undefined,
    requireExecution: true,
  })
  // On success clear the draft; on failure keep everything for one-click retry.
  if (action) cancelDraft()
}

function fieldError(field) {
  const e = (store.saveFieldErrors || []).find((x) => x.field === field)
  return e ? e.message : null
}

function start(act) { store.updateAction(props.id, act.id, { status: 'in_progress' }) }
function dismiss(act) { store.updateAction(props.id, act.id, { status: 'dismissed' }) }
function openVerify(act) { verifyingId.value = act.id; verifyValue.value = ''; verifyNote.value = '' }
async function submitVerify(act) {
  const body = {}
  if (verifyValue.value !== '' && verifyValue.value != null) body.postValue = Number(verifyValue.value)
  if (verifyNote.value) body.note = verifyNote.value
  const res = await store.verifyAction(props.id, act.id, body)
  if (res) verifyingId.value = null
}

function toggleConfig() {
  configOpen.value = !configOpen.value
  if (configOpen.value) {
    // Seed the config editor from the full card catalog with current visibility.
    const all = store.workspace?.cards || []
    const visibleKeys = store.workspace?.config?.visibleCards || []
    const ordered = [
      ...visibleKeys.map((k) => all.find((c) => c.metricKey === k)).filter(Boolean),
      ...all.filter((c) => !visibleKeys.includes(c.metricKey)),
    ]
    configDraft.value = ordered.map((c) => ({ key: c.metricKey, label: c.label, visible: visibleKeys.includes(c.metricKey) }))
  }
}
function move(idx, dir) {
  const next = idx + dir
  if (next < 0 || next >= configDraft.value.length) return
  const arr = configDraft.value
  ;[arr[idx], arr[next]] = [arr[next], arr[idx]]
}
async function saveConfig() {
  const visibleCards = configDraft.value.filter((c) => c.visible).map((c) => c.key)
  if (!visibleCards.length) { store.saveError = 'Select at least one card.'; return }
  const ok = await store.saveConfig(props.id, { visibleCards })
  if (ok) configOpen.value = false
}

function isFinalized(status) { return status === 'verified' || status === 'dismissed' }
function isVerifiable(status) { return status === 'in_progress' || status === 'awaiting_verification' || status === 'not_verified' }
function statusLabel(s) { return ({ queued: 'Queued', in_progress: 'In progress', awaiting_verification: 'Awaiting verification', verified: 'Verified', not_verified: 'Not verified', dismissed: 'Dismissed' })[s] || s }

// ── formatting (deterministic + null-safe — US-64 AC3) ──────────────────────
function formatValue(v, card) {
  if (v == null || Number.isNaN(v)) return '—'
  const unit = card?.unit
  if (unit === '$') return '$' + Number(v).toLocaleString()
  if (unit === '%') return Number(v).toFixed(card.decimals ?? 1) + '%'
  return Number(v).toLocaleString()
}
function formatPct(p) {
  if (p == null || Number.isNaN(p)) return '—'
  const pct = (p * 100)
  return (pct > 0 ? '+' : '') + pct.toFixed(1) + '%'
}
function arrow(dir) { return dir === 'up' ? '▲' : dir === 'down' ? '▼' : '▬' }
function signalClass(card) {
  if (!card || !card.sufficientData) return 'aw-signal-flat'
  return card.improving ? 'aw-signal-good' : (card.isAdverse ? 'aw-signal-bad' : 'aw-signal-flat')
}
function ctxVal(v) { return v == null || v === '' ? 'Unavailable' : v }
function cardFreshness(card) {
  // Per-card freshness with an explicit 3-state result (US-60 AC4).
  const ts = store.freshness?.lastIngestAt
  if (!ts) return { state: 'unknown', label: 'Freshness: Unknown' }
  const age = Date.now() - Date.parse(ts)
  if (Number.isNaN(age)) return { state: 'unknown', label: 'Freshness: Unknown' }
  if (age > STALE_MS) return { state: 'stale', label: 'Freshness: Stale' }
  return { state: 'healthy', label: 'Freshness: Live' }
}
function relativeTime(ts) {
  if (!ts) return ''
  const age = Date.now() - Date.parse(ts)
  if (Number.isNaN(age)) return ''
  const mins = Math.round(age / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  return `${Math.round(mins / 60)}h ago`
}

onMounted(() => { store.loadWorkspace(props.id) })
// Re-load when navigating between sites without unmounting.
watch(() => props.id, (id) => { store.closeDrilldown(); store.loadWorkspace(id) })
</script>

<style scoped>
.aw-page { max-width: 1200px; margin: 0 auto; padding: 24px 20px 64px; }
.aw-sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
.aw-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
.aw-kicker { font-size: 12px; letter-spacing: .08em; text-transform: uppercase; color: #6b7280; margin: 0; }
.aw-title { font-size: 28px; font-weight: 700; margin: 2px 0 0; color: #111827; outline: none; }
.aw-header-actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.aw-freshness { display: inline-flex; align-items: center; gap: 6px; font-size: 13px; padding: 4px 10px; border-radius: 999px; }
.aw-fresh-healthy { background: #ecfdf5; color: #047857; }
.aw-fresh-stale { background: #fef2f2; color: #b91c1c; }
.aw-fresh-dot { width: 8px; height: 8px; border-radius: 50%; background: currentColor; }
.aw-fresh-meta { color: inherit; opacity: .7; }

.aw-btn { border: 1px solid #d1d5db; background: #fff; color: #111827; border-radius: 8px; padding: 8px 14px; font-size: 14px; cursor: pointer; }
.aw-btn:hover { background: #f9fafb; }
.aw-btn:disabled { opacity: .5; cursor: not-allowed; }
.aw-btn-primary { background: #4f46e5; border-color: #4f46e5; color: #fff; }
.aw-btn-primary:hover { background: #4338ca; }
.aw-btn-ghost { background: transparent; }
.aw-btn-sm { padding: 5px 10px; font-size: 13px; }

.aw-config { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; margin-bottom: 20px; background: #fafafa; }
.aw-config-list { list-style: none; padding: 0; margin: 8px 0; }
.aw-config-row { display: flex; justify-content: space-between; align-items: center; padding: 6px 0; }
.aw-config-check { display: flex; align-items: center; gap: 8px; }
.aw-icon-btn { border: 1px solid #d1d5db; background: #fff; border-radius: 6px; width: 28px; height: 28px; cursor: pointer; }
.aw-icon-btn:disabled { opacity: .4; cursor: not-allowed; }
.aw-config-actions { display: flex; gap: 8px; margin-top: 8px; }

.aw-body { min-height: 200px; }
.aw-state { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 56px 16px; text-align: center; color: #6b7280; }
.aw-state-error { color: #b91c1c; }
.aw-state-empty h2 { color: #111827; margin: 0; }
.aw-spinner { width: 32px; height: 32px; border: 3px solid #e5e7eb; border-top-color: #4f46e5; border-radius: 50%; animation: aw-spin 0.8s linear infinite; }
@keyframes aw-spin { to { transform: rotate(360deg); } }

.aw-cards { display: grid; grid-template-columns: 1fr; gap: 14px; margin-bottom: 24px; }
@media (min-width: 640px) { .aw-cards { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .aw-cards { grid-template-columns: repeat(3, 1fr); } }
.aw-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; background: #fff; display: flex; flex-direction: column; gap: 6px; }
.aw-card-adverse { border-color: #fca5a5; }
.aw-card-top { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.aw-card-label { font-size: 13px; color: #6b7280; font-weight: 600; }
.aw-card-fresh { font-size: 11px; padding: 2px 6px; border-radius: 6px; }
.aw-cf-healthy { background: #ecfdf5; color: #047857; }
.aw-cf-stale { background: #fef2f2; color: #b91c1c; }
.aw-cf-unknown { background: #f3f4f6; color: #6b7280; }
.aw-card-value { font-size: 26px; font-weight: 700; color: #111827; }
.aw-card-signal { font-size: 14px; font-weight: 600; }
.aw-signal-good { color: #047857; }
.aw-signal-bad { color: #b91c1c; }
.aw-signal-flat { color: #6b7280; }
.aw-card-conf { color: #9ca3af; font-weight: 500; }
.aw-card-cta { align-self: flex-start; margin-top: 6px; background: transparent; border: none; color: #4f46e5; font-weight: 600; cursor: pointer; padding: 0; font-size: 14px; }

.aw-columns { display: grid; grid-template-columns: 1fr; gap: 20px; }
@media (min-width: 900px) { .aw-columns { grid-template-columns: 1fr 1fr; } }
.aw-panel { border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; background: #fff; }
.aw-section-title { font-size: 16px; font-weight: 700; margin: 0 0 12px; color: #111827; }
.aw-count { color: #6b7280; font-weight: 500; }
.aw-muted { color: #9ca3af; font-size: 14px; }
.aw-list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 12px; }

.aw-anomaly, .aw-action, .aw-rec { border: 1px solid #f3f4f6; border-radius: 10px; padding: 12px; }
.aw-anomaly-head, .aw-action-head { display: flex; align-items: center; gap: 8px; }
.aw-sev { font-size: 11px; text-transform: uppercase; font-weight: 700; padding: 2px 8px; border-radius: 6px; }
.aw-sev-high { background: #fef2f2; color: #b91c1c; }
.aw-sev-medium { background: #fffbeb; color: #b45309; }
.aw-sev-low { background: #f3f4f6; color: #6b7280; }
.aw-anomaly-label { font-weight: 600; flex: 1; }
.aw-dest { font-size: 11px; padding: 2px 8px; border-radius: 6px; }
.aw-dest-auto_queue { background: #eef2ff; color: #4338ca; }
.aw-dest-manual_triage { background: #f3f4f6; color: #374151; }
.aw-anomaly-reason { font-size: 13px; color: #6b7280; margin: 6px 0; }

.aw-action-title { font-weight: 600; margin: 6px 0 4px; }
.aw-action-meta { display: flex; gap: 10px; font-size: 13px; color: #6b7280; align-items: center; flex-wrap: wrap; }
.aw-status { font-size: 11px; font-weight: 700; padding: 2px 8px; border-radius: 6px; background: #eef2ff; color: #4338ca; }
.aw-status-verified { background: #ecfdf5; color: #047857; }
.aw-status-not_verified { background: #fef2f2; color: #b91c1c; }
.aw-status-dismissed { background: #f3f4f6; color: #6b7280; }
.aw-action-src { font-size: 11px; color: #9ca3af; text-transform: uppercase; }
.aw-prio { text-transform: capitalize; }
.aw-action-controls { display: flex; gap: 6px; margin-top: 8px; flex-wrap: wrap; }
.aw-verif { font-size: 13px; font-weight: 600; margin: 6px 0 0; }
.aw-verif-verified { color: #047857; }
.aw-verif-not_verified { color: #b91c1c; }
.aw-verify-form { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
.aw-degraded { margin-top: 12px; font-size: 13px; color: #b45309; background: #fffbeb; padding: 8px 10px; border-radius: 8px; }

.aw-drawer { position: fixed; top: 0; right: 0; width: min(460px, 100vw); height: 100vh; background: #fff; box-shadow: -8px 0 30px rgba(0,0,0,.12); padding: 20px; overflow-y: auto; z-index: 50; }
.aw-drawer-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }
.aw-drawer-title { font-size: 20px; font-weight: 700; margin: 0; outline: none; }
.aw-context { display: grid; grid-template-columns: 1fr; gap: 6px; margin: 0 0 16px; }
.aw-context-row { display: flex; justify-content: space-between; border-bottom: 1px solid #f3f4f6; padding: 6px 0; }
.aw-context-row dt { color: #6b7280; font-size: 13px; margin: 0; }
.aw-context-row dd { margin: 0; font-weight: 600; font-size: 14px; }
.aw-sub { font-size: 14px; font-weight: 700; margin: 16px 0 8px; }
.aw-drivers ul { margin: 0; padding-left: 18px; color: #374151; font-size: 13px; }
.aw-rec-head { display: flex; justify-content: space-between; gap: 8px; }
.aw-rec-title { font-weight: 600; }
.aw-rec-impact { font-size: 12px; color: #6b7280; white-space: nowrap; }
.aw-rec-rationale { font-size: 13px; color: #6b7280; margin: 6px 0; }
.aw-rec-active { border-color: #a5b4fc; background: #f5f7ff; }

.aw-draft { margin-top: 16px; border-top: 1px solid #e5e7eb; padding-top: 12px; display: flex; flex-direction: column; }
.aw-field-label { font-size: 13px; font-weight: 600; color: #374151; margin: 10px 0 4px; }
.aw-req { color: #dc2626; }
.aw-input { border: 1px solid #d1d5db; border-radius: 8px; padding: 8px 10px; font-size: 14px; width: 100%; }
.aw-field-err { color: #b91c1c; font-size: 12px; margin: 4px 0 0; }
.aw-readonly { font-size: 12px; color: #6b7280; margin: 10px 0 0; font-style: italic; }
.aw-draft-actions { display: flex; gap: 8px; margin-top: 12px; }
.aw-save-ok { color: #047857; font-size: 13px; margin: 8px 0 0; }
.aw-save-err { color: #b91c1c; font-size: 13px; margin: 8px 0 0; }
</style>
